/**
 * The Colour Match email gate: behaviour for snippets/jessie-email-gate.liquid.
 *
 * Lifted from sections/material-colour-match.liquid so that a shopper meets
 * one gate with one promise across the site, and so that unlocking on
 * /pages/colour-match carries to a product page and back — the sessionStorage
 * keys are the live tool's global ones (ps_cm_unlocked / ps_cm_email /
 * ps_cm_input) and are deliberately NOT namespaced.
 *
 * TICKED   The real customer form posts natively to /contact with the tags
 *          "<tag>,colour-match-input:<typed>". Shopify subscribes the address
 *          (captcha first if it suspects a bot) and returns to the page with
 *          ?customer_posted=true. Never AJAX: a scripted POST gets a 403 bot
 *          challenge, which is why nothing here calls preventDefault on the
 *          consented path.
 * UNTICKED Unlock in place; nothing is sent anywhere.
 *
 * Shopify's storefront captcha hooks the submit event of every customer form
 * and runs its challenge even when another listener called preventDefault,
 * then posts the form itself. So the unticked path must never raise a submit
 * event: it is decided on the button click, and only the ticked path lets the
 * click go on to submit. Enter in the email field is forwarded to that same
 * click rather than deciding anything itself, so one keypress is one unlock.
 *
 * WHAT IS NEW HERE. The live section reads the unlock state once at init, so
 * two gates on one page do not agree until a reload. Every unlock here
 * dispatches `jessie:gate-unlocked` on window and every instance listens, so
 * the PDP callout and a band on the same page unlock together.
 *
 * This file may be emitted twice — two blocks on one page, each asking for the
 * script it needs — and a repeated src re-executes, which would mean two
 * submit handlers and two colour_match_unlock events. Hence the guard below
 * and the data-jessie-filled marker on each wired host.
 */
(function () {
  'use strict';

  if (window.__jessie_gate) return;
  window.__jessie_gate = true;

  var KEYS = { unlocked: 'ps_cm_unlocked', email: 'ps_cm_email', input: 'ps_cm_input' };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var UNLOCK_EVENT = 'jessie:gate-unlocked';
  var postedHandled = false;

  function read(key) {
    try {
      return sessionStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }

  function write(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* Private mode, or storage disabled: the unlock simply does not persist. */
    }
  }

  function isUnlocked() {
    return read(KEYS.unlocked) === '1';
  }

  function track(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      for (var key in data || {}) {
        if (Object.prototype.hasOwnProperty.call(data, key)) payload[key] = data[key];
      }
      window.dataLayer.push(payload);
    } catch (e) {
      /* Analytics must never break the form. */
    }
  }

  /**
   * The colour the shopper typed, from the element the host names.
   *
   * It is normally a sibling — the band's or the callout's colour input — so
   * the search widens from the host to the nearest [data-jessie-gate-scope]
   * and only then to the document. Two callouts on one page each want their
   * own input, which is what the scope attribute is for.
   */
  function typedValue(host) {
    var selector = host.getAttribute('data-jessie-gate-input-selector') || '[data-jessie-gate-input]';
    var scope = host.closest('[data-jessie-gate-scope]') || document;
    var el = host.querySelector(selector) || scope.querySelector(selector);
    if (!el) return '';
    var value = el.value !== undefined && el.value !== null ? el.value : el.textContent;
    return String(value || '').trim();
  }

  /** Remember the unlock, tell every other gate on the page, tell analytics. */
  function unlock(email, consent, typed) {
    write(KEYS.unlocked, '1');
    if (email) write(KEYS.email, email);
    if (typed) write(KEYS.input, typed);
    track('colour_match_unlock', { accepts_marketing: !!consent });
    broadcast(email, consent);
  }

  function broadcast(email, consent) {
    var detail = { email: email || read(KEYS.email), accepts_marketing: !!consent };
    var event;
    try {
      event = new CustomEvent(UNLOCK_EVENT, { detail: detail });
    } catch (e) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(UNLOCK_EVENT, false, false, detail);
    }
    window.dispatchEvent(event);
  }

  /** One class on the host is the whole locked/unlocked state. */
  function applyState(host) {
    var unlocked = isUnlocked();
    host.classList.toggle('is-locked', !unlocked);
    host.classList.toggle('is-unlocked', unlocked);
  }

  function wire(host) {
    var form = host.querySelector('form');
    if (!form) return;
    var email = form.querySelector('[data-jessie-gate-email]');
    var consent = form.querySelector('[data-jessie-gate-consent]');
    var tags = form.querySelector('[data-jessie-gate-tags]');
    var error = form.querySelector('[data-jessie-gate-error]');
    var button = form.querySelector('button[type=submit]');
    if (!email || !button) return;

    var baseTag = tags ? tags.value : 'colour-match';

    function go(event) {
      var address = email.value.trim();
      if (!address || !EMAIL_RE.test(address)) {
        event.preventDefault();
        if (error) error.textContent = 'Enter a valid email address to see your matches.';
        email.setAttribute('aria-invalid', 'true');
        email.focus();
        return;
      }
      if (error) error.textContent = '';
      email.removeAttribute('aria-invalid');

      /* Enter in the email field is handed to the button before anything is
         unlocked, so one keypress produces one unlock. Going the other way —
         unlocking here and then clicking — runs this function twice and pushes
         two colour_match_unlock events (§1.6 rule 7). preventDefault stops the
         browser's own implicit submission, which would bypass the unticked
         branch entirely. */
      if (event.type === 'keydown') {
        event.preventDefault();
        button.click();
        return;
      }

      var ticked = consent ? consent.checked : false;
      var typed = typedValue(host);
      unlock(address, ticked, typed);

      if (!ticked) {
        /* Nothing is sent anywhere: the box is not ticked. */
        event.preventDefault();
        return;
      }

      if (tags) tags.value = baseTag + ',colour-match-input:' + typed.replace(/[^#\w .-]/g, '');
      /* The click goes on to submit and the browser posts natively to /contact. */
    }

    button.addEventListener('click', go);
    email.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') go(event);
    });
    form.addEventListener('submit', function (event) {
      if (consent && !consent.checked) event.preventDefault();
    });
  }

  function init() {
    var hosts = document.querySelectorAll('[data-jessie-gate]');
    if (!hosts.length) return;

    /* Coming back from Shopify's own subscribe round trip. Once per page load,
       however many gates are on the page. */
    if (!postedHandled && /[?&]customer_posted=true/.test(window.location.search)) {
      postedHandled = true;
      write(KEYS.unlocked, '1');
      track('colour_match_subscribed', {});
      broadcast(read(KEYS.email), true);
    }

    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (!host.hasAttribute('data-jessie-filled')) {
        host.setAttribute('data-jessie-filled', '');
        wire(host);
      }
      applyState(host);
    }
  }

  window.addEventListener(UNLOCK_EVENT, function () {
    var hosts = document.querySelectorAll('[data-jessie-gate]');
    for (var i = 0; i < hosts.length; i++) applyState(hosts[i]);
  });

  /* _filament-color-switcher.liquid swaps the product section over AJAX and
     dispatches this; the gate inside the new markup has to be wired again. */
  window.addEventListener('product:section-swapped', init);

  window.JessieGate = {
    KEYS: KEYS,
    init: init,
    isUnlocked: isUnlocked,
    email: function () {
      return read(KEYS.email);
    },
    input: function () {
      return read(KEYS.input);
    },
    /* Both new surfaces write the typed colour before handing over to
       /pages/colour-match: the live tool reads this key on load and has no
       query-string input, so a plain link would lose what was typed. */
    setInput: function (value) {
      write(KEYS.input, String(value || '').trim());
    },
    unlock: unlock,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
